import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { getIn, useFormikContext } from "formik";
import React from "react";

import { CD80DDetails } from "..";
import { MaximumLimits, RegimeTypes } from "../../../../config/filingConstants";
import { IFormITR1 } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PriceDisplay from "../../../PriceDisplay";
import { FormField, NumericInputField } from "../../../fields";

const CDCommonDeductionsForm: React.FC = () => {
	const { values, touched, errors, setFieldValue, handleBlur } = useFormikContext<IFormITR1>();
	const RegimeType = values?.FilingReturnInfo?.RegimeType || "";

	const dispatch = useAppDispatch();

	const handleTotalCalculation = (section: string, amount: number) => {
		const _Total = Object.entries(values.DeductUndChapVIA).reduce((curr, [key, value]) => {
			if (key === section) {
				return Number(curr) + Number(amount);
			}
			return Number(curr) + Number(value);
		}, 0);

		setFieldValue("TotalChapVIADeductions", _Total);
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80C - Investments
						<span
							className="info-wrap"
							role="button"
							onClick={() => {
								dispatch(
									showHintModal({
										title: "Deductions under Section 80C",
										componentName: "Investment80CHint"
									})
								);
							}}
						>
							<FASvgIcon icon={faInfoCircle} />
						</span>
						<small>Deductions for PPF, Life Insurance, ELSS Mutual Funds etc.</small>
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80C} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Total 80C Deductions" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="CommonDeductions.TotalSection80C"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.CommonDeductions.TotalSection80C
						}
						onBlur={handleBlur("CommonDeductions.TotalSection80C")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("CommonDeductions.TotalSection80C", value);
							setFieldValue("DeductUndChapVIA.Section80C", Number(value));
							handleTotalCalculation("Section80C", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `CommonDeductions.TotalSection80C`)) &&
							getIn(touched, `CommonDeductions.TotalSection80C`)
						}
						errorMsg={getIn(errors, `CommonDeductions.TotalSection80C`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>

			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80CCC - Contribution to Pension Plan / Annuity
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80CCC} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Total Contribution amount to Pension Plan / Annuity" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="CommonDeductions.TotalSection80CCC"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.CommonDeductions.TotalSection80CCC
						}
						onBlur={handleBlur("CommonDeductions.TotalSection80CCC")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("CommonDeductions.TotalSection80CCC", value);
							setFieldValue("DeductUndChapVIA.Section80CCC", Number(value));
							handleTotalCalculation("Section80CCC", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `CommonDeductions.TotalSection80CCC`)) &&
							getIn(touched, `CommonDeductions.TotalSection80CCC`)
						}
						errorMsg={getIn(errors, `CommonDeductions.TotalSection80CCC`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>

			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>80CCD(1) and 80CCD(1B) - Employee Contribution to New Pension Scheme (NPS)</h5>
				</div>
				<FormField label="Your contribution to NPS" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="CommonDeductions.TotalSection80CCD1Employee"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.CommonDeductions.TotalSection80CCD1Employee
						}
						onBlur={handleBlur("CommonDeductions.TotalSection80CCD1Employee")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("CommonDeductions.TotalSection80CCD1Employee", value);
							setFieldValue("DeductUndChapVIA.Section80CCDEmployeeOrSE", Number(value));
							handleTotalCalculation("Section80CCDEmployeeOrSE", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `CommonDeductions.TotalSection80CCD1Employee`)) &&
							getIn(touched, `CommonDeductions.TotalSection80CCD1Employee`)
						}
						errorMsg={getIn(errors, `CommonDeductions.TotalSection80CCD1Employee`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>

			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80CCD(2) - Employer contribution to NPS
						<small>Deduction will be restricted to 10% of Salary(Basic + DA)</small>
					</h5>
				</div>
				<FormField label="Employer contribution to NPS" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="CommonDeductions.TotalSection80CCD2Employer"
						value={values.CommonDeductions.TotalSection80CCD2Employer}
						onBlur={handleBlur("CommonDeductions.TotalSection80CCD2Employer")}
						onChange={(value) => {
							setFieldValue("CommonDeductions.TotalSection80CCD2Employer", value);
							setFieldValue("DeductUndChapVIA.Section80CCDEmployer", Number(value));
							handleTotalCalculation("Section80CCDEmployer", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `CommonDeductions.TotalSection80CCD2Employer`)) &&
							getIn(touched, `CommonDeductions.TotalSection80CCD2Employer`)
						}
						errorMsg={getIn(errors, `CommonDeductions.TotalSection80CCD2Employer`)}
					/>
				</FormField>
			</section>

			<CD80DDetails />

			<section>
				<div className="section-title-wrap">
					<h5>
						80TTA - Deduction for Interest earned on Savings Bank Account and Deposits
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80TTA} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Interest earned on Savings Bank Account and Deposits" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="CommonDeductions.TotalSection80TTA"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.CommonDeductions.TotalSection80TTA
						}
						onBlur={handleBlur("CommonDeductions.TotalSection80TTA")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("CommonDeductions.TotalSection80TTA", value);
							setFieldValue("DeductUndChapVIA.Section80TTA", Number(value));
							handleTotalCalculation("Section80TTA", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `CommonDeductions.TotalSection80TTA`)) &&
							getIn(touched, `CommonDeductions.TotalSection80TTA`)
						}
						errorMsg={getIn(errors, `CommonDeductions.TotalSection80TTA`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
		</>
	);
};

export default CDCommonDeductionsForm;
